export type SolarCauseLocal = {
  cause: string;
  probability: number;
};

export type SolarFaultLocal = {
  id: string;
  category_id: string | null;
  device_ar: string;
  name_ar: string;
  description_ar: string;
  symptoms: string[];
  causes: SolarCauseLocal[];
  tests: { test: string; location: string; method: string; normal: string; faulty: string }[];
  solutions: string[];
  severity: string;
  safety_warnings: string[];
  error_code: string | null;
  keywords: string;
  next_question_key: string | null;
};

export type SolarIssueLocal = {
  id: string; title_ar: string; description_ar: string; causes: SolarCauseLocal[];
  steps: string[]; measurements: string[]; solution_ar: string; severity: string;
  safety_ar: string; keywords: string; sort_order: number;
};

type Row = [string,string,string,string,string,SolarCauseLocal[],string[],string,string];

const C = (cause: string, probability: number): SolarCauseLocal => ({ cause, probability });

const rows: Row[] = [
  [
    "solar-001",
    "الألواح الشمسية",
    "المنظومة ما تنتج كهرباء من الألواح",
    "هذا العطل يتعلق بـ الألواح الشمسية: المنظومة ما تنتج كهرباء من الألواح. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "المنظومة ما تنتج كهرباء من الألواح",
    [C("قاطع أو عازل DC مفصول", 45), C("قطع رئيسي بكيبلات الألواح", 35), C("دخل PV ما يوصل للإنفرتر", 20)],
    ["افحص حالة القواطع والعوازل من الخارج.", "خلي فني يفحص فولت السترنكات والتوصيلات."],
    "حرجة",
    "PV no production zero output",
  ],
  [
    "solar-002",
    "الألواح الشمسية",
    "إنتاج الألواح أقل من الطبيعي",
    "هذا العطل يتعلق بـ الألواح الشمسية: إنتاج الألواح أقل من الطبيعي. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "إنتاج الألواح أقل من الطبيعي",
    [C("غبار وأوساخ على الألواح", 45), C("ظل جزئي", 35), C("سترنك أو لوح ضعيف", 20)],
    ["نظف الألواح بطريقة مناسبة وآمنة.", "قارن السترنكات بنفس الظروف."],
    "متوسطة",
    "PV low production",
  ],
  [
    "solar-003",
    "الألواح الشمسية",
    "الغبار مأثر على الإنتاج",
    "هذا العطل يتعلق بـ الألواح الشمسية: الغبار مأثر على الإنتاج. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "الغبار مأثر على الإنتاج",
    [C("تراكم غبار", 45), C("قلة التنظيف", 35), C("موقع بيه أتربة قوية", 20)],
    ["نظف الألواح حسب حالة الموقع.", "راقب الفرق بالإنتاج قبل وبعد التنظيف."],
    "منخفضة",
    "PV soiling dust",
  ],
  [
    "solar-004",
    "الألواح الشمسية",
    "ظل جزئي على الألواح",
    "هذا العطل يتعلق بـ الألواح الشمسية: ظل جزئي على الألواح. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "ظل جزئي على الألواح",
    [C("ظل بناية أو جدار", 45), C("شجرة أو عمود", 35), C("ظل صف ألواح ثاني", 20)],
    ["حدد وقت ومكان الظل.", "عالج مصدر الظل أو ترتيب الصفوف."],
    "متوسطة",
    "PV partial shading",
  ],
  [
    "solar-005",
    "الألواح الشمسية",
    "أكو Hot Spot باللوح",
    "هذا العطل يتعلق بـ الألواح الشمسية: أكو Hot Spot باللوح. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "أكو Hot Spot باللوح",
    [C("خلية متضررة", 45), C("ظل جزئي مستمر", 35), C("دايود Bypass متضرر", 20)],
    ["وقف تحميل اللوح إذا الضرر واضح.", "خلي فني يفحصه بالكاميرا الحرارية أو اختبار مناسب."],
    "عالية",
    "PV hot spot",
  ],
  [
    "solar-006",
    "الألواح الشمسية",
    "زجاج اللوح مكسور",
    "هذا العطل يتعلق بـ الألواح الشمسية: زجاج اللوح مكسور. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "زجاج اللوح مكسور",
    [C("ضربة أو حجر", 45), C("برد قوي", 35), C("تحميل أو نقل غلط", 20)],
    ["لا تلمس اللوح إذا أكو رطوبة أو عزل مكشوف.", "بدل اللوح إذا العزل أو السلامة متأثرة."],
    "حرجة",
    "PV cracked glass",
  ],
  [
    "solar-007",
    "الألواح الشمسية",
    "الخلايا بيها Microcracks",
    "هذا العطل يتعلق بـ الألواح الشمسية: الخلايا بيها Microcracks. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "الخلايا بيها Microcracks",
    [C("نقل أو تركيب خشن", 45), C("تحميل ميكانيكي", 35), C("إجهاد حراري أو رياح", 20)],
    ["قارن إنتاج اللوح بالباقي.", "خلي جهة مختصة تفحصه إذا الفرق واضح."],
    "متوسطة",
    "PV microcracks",
  ],
  [
    "solar-008",
    "الألواح الشمسية",
    "طبقات اللوح بدت تنفصل",
    "هذا العطل يتعلق بـ الألواح الشمسية: طبقات اللوح بدت تنفصل. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "طبقات اللوح بدت تنفصل",
    [C("تقادم اللوح", 45), C("حرارة ورطوبة", 35), C("عيب مواد أو تصنيع", 20)],
    ["راقب العزل والإنتاج.", "بدل اللوح إذا الانفصال مأثر على السلامة."],
    "عالية",
    "PV delamination",
  ],
  [
    "solar-009",
    "الألواح الشمسية",
    "لون اللوح متغير أو مصفر",
    "هذا العطل يتعلق بـ الألواح الشمسية: لون اللوح متغير أو مصفر. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "لون اللوح متغير أو مصفر",
    [C("تقادم مواد التغليف", 45), C("حرارة وUV", 35), C("رطوبة", 20)],
    ["قارن إنتاج اللوح ويا لوح سليم.", "افحصه إذا التغير كبير."],
    "متوسطة",
    "PV discoloration yellowing",
  ],
  [
    "solar-010",
    "علبة توصيل اللوح",
    "حرارة Junction Box ترتفع",
    "هذا العطل يتعلق بـ علبة توصيل اللوح: حرارة Junction Box ترتفع. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "حرارة Junction Box ترتفع",
    [C("ربط داخلي ضعيف", 45), C("دايود Bypass متضرر", 35), C("قوس DC", 20)],
    ["لا تفتح العلبة وهي حية.", "خلي فني يفحصها أو يبدل اللوح حسب التصميم."],
    "حرجة",
    "PV junction box overheating",
  ],
  [
    "solar-011",
    "دايود Bypass",
    "دايود Bypass مفتوح",
    "هذا العطل يتعلق بـ دايود Bypass: دايود Bypass مفتوح. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "دايود Bypass مفتوح",
    [C("الدايود محترق", 45), C("حرارة عالية", 35), C("عمر أو عيب بالقطعة", 20)],
    ["خلي فني يفحص الدايود بعد العزل.", "بدل الـ Junction Box أو اللوح حسب التصميم."],
    "عالية",
    "bypass diode open",
  ],
  [
    "solar-012",
    "دايود Bypass",
    "دايود Bypass صار شورت",
    "هذا العطل يتعلق بـ دايود Bypass: دايود Bypass صار شورت. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "دايود Bypass صار شورت",
    [C("الدايود انهار داخلياً", 45), C("نبضة جهد", 35), C("حرارة عالية", 20)],
    ["قارن فولت اللوح بغيره.", "خلي فني يفحص الدايود."],
    "عالية",
    "bypass diode shorted",
  ],
  [
    "solar-013",
    "إطار اللوح",
    "أرضي إطار اللوح مقطوع",
    "هذا العطل يتعلق بـ إطار اللوح: أرضي إطار اللوح مقطوع. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "أرضي إطار اللوح مقطوع",
    [C("سلك الأرضي مفصول", 45), C("كلامب الأرضي مرخي", 35), C("أكسدة بنقطة الربط", 20)],
    ["خلي فني يفحص استمرارية الأرضي.", "صلح الربط بقطع مناسبة."],
    "حرجة",
    "PV module grounding",
  ],
  [
    "solar-014",
    "الألواح الشمسية",
    "رطوبة داخلة للوح",
    "هذا العطل يتعلق بـ الألواح الشمسية: رطوبة داخلة للوح. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "رطوبة داخلة للوح",
    [C("سيلات اللوح متضررة", 45), C("تشققات بالـ Backsheet", 35), C("دخول ماء من Junction Box", 20)],
    ["خلي اللوح مفصول إذا العزل مشكوك بيه.", "بدل اللوح إذا دخل الماء للداخل."],
    "حرجة",
    "PV moisture ingress",
  ],
  [
    "solar-015",
    "الألواح الشمسية",
    "PID مأثر على الألواح",
    "هذا العطل يتعلق بـ الألواح الشمسية: PID مأثر على الألواح. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "PID مأثر على الألواح",
    [C("فرق جهد عالي بالنسبة للأرضي", 45), C("رطوبة وحرارة", 35), C("نوع لوح معرض للـ PID", 20)],
    ["قارن الأداء على مدى زمني.", "خلي مختص يحدد إذا المشكلة PID."],
    "عالية",
    "PV PID potential induced degradation",
  ],
  [
    "solar-016",
    "الألواح الشمسية",
    "أكو تآكل أو Snail Trails بالخلايا",
    "هذا العطل يتعلق بـ الألواح الشمسية: أكو تآكل أو Snail Trails بالخلايا. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "أكو تآكل أو Snail Trails بالخلايا",
    [C("Microcracks مع رطوبة", 45), C("تآكل مواد داخلية", 35), C("تقادم اللوح", 20)],
    ["راقب إنتاج اللوح.", "بدله إذا الأداء أو العزل تأثر."],
    "متوسطة",
    "PV snail trails corrosion",
  ],
  [
    "solar-017",
    "الألواح الشمسية",
    "لوح واحد إنتاجه أقل من الباقي",
    "هذا العطل يتعلق بـ الألواح الشمسية: لوح واحد إنتاجه أقل من الباقي. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "لوح واحد إنتاجه أقل من الباقي",
    [C("تضرر خلايا", 45), C("ظل محلي", 35), C("دايود Bypass متضرر", 20)],
    ["نظف وافحص الظل.", "قارن فولت وتيار اللوح بواسطة فني."],
    "متوسطة",
    "one PV module low output",
  ],
  [
    "solar-018",
    "هيكل الألواح",
    "براغي الهيكل مرخية",
    "هذا العطل يتعلق بـ هيكل الألواح: براغي الهيكل مرخية. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "براغي الهيكل مرخية",
    [C("اهتزاز ورياح", 45), C("شد غير صحيح وقت التركيب", 35), C("تآكل بالقطع", 20)],
    ["خلي جهة تركيب تفحص الهيكل.", "لا تصعد للسطح بدون حماية سقوط مناسبة."],
    "عالية",
    "PV racking loose bolts",
  ],
  [
    "solar-019",
    "هيكل الألواح",
    "الهيكل متضرر من الريح أو العاصفة",
    "هذا العطل يتعلق بـ هيكل الألواح: الهيكل متضرر من الريح أو العاصفة. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "الهيكل متضرر من الريح أو العاصفة",
    [C("رياح قوية", 45), C("تثبيت غير كافي", 35), C("ضعف أو تآكل بالهيكل", 20)],
    ["اعزل المنطقة الخطرة.", "خلي جهة مختصة تفحص الهيكل قبل التشغيل."],
    "حرجة",
    "PV wind storm damage",
  ],
  [
    "solar-020",
    "الألواح الشمسية",
    "صف ألواح يظلل الصف اللي وراه",
    "هذا العطل يتعلق بـ الألواح الشمسية: صف ألواح يظلل الصف اللي وراه. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "صف ألواح يظلل الصف اللي وراه",
    [C("المسافة بين الصفوف قليلة", 45), C("زاوية الميل مو مناسبة", 35), C("ترتيب الموقع تغير", 20)],
    ["راقب الظل على مدار اليوم.", "راجع المسافات والزوايا."],
    "متوسطة",
    "PV row shading",
  ],
  [
    "solar-021",
    "سترنك الألواح",
    "سترنك كامل ما يطلع تيار",
    "هذا العطل يتعلق بـ سترنك الألواح: سترنك كامل ما يطلع تيار. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "سترنك كامل ما يطلع تيار",
    [C("فيوز السترنك فاصل", 45), C("قطع بكيبل DC", 35), C("MC4 مفصول", 20)],
    ["خلي فني يعزل السترنك ويفحص مسار التيار.", "لا تفتح MC4 أو فيوز تحت حمل."],
    "حرجة",
    "PV string zero current",
  ],
  [
    "solar-022",
    "سترنك الألواح",
    "تيار سترنك أقل من الباقي",
    "هذا العطل يتعلق بـ سترنك الألواح: تيار سترنك أقل من الباقي. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "تيار سترنك أقل من الباقي",
    [C("ظل أو غبار", 45), C("لوح ضعيف", 35), C("MC4 أو ربط مقاومته عالية", 20)],
    ["قارن السترنكات بنفس الظروف.", "افحص الألواح والكونكترات."],
    "عالية",
    "PV string low current",
  ],
  [
    "solar-023",
    "سترنك الألواح",
    "فولت السترنك أقل من الطبيعي",
    "هذا العطل يتعلق بـ سترنك الألواح: فولت السترنك أقل من الطبيعي. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "فولت السترنك أقل من الطبيعي",
    [C("لوح متجاوز بسبب Bypass", 45), C("لوح مو داخل بالسترنك", 35), C("توصيل غلط", 20)],
    ["راجع عدد الألواح والتوصيل.", "قارن فولت السترنكات."],
    "عالية",
    "PV string low voltage",
  ],
  [
    "solar-024",
    "سترنك الألواح",
    "فولت السترنك أعلى من تحمل الإنفرتر",
    "هذا العطل يتعلق بـ سترنك الألواح: فولت السترنك أعلى من تحمل الإنفرتر. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "فولت السترنك أعلى من تحمل الإنفرتر",
    [C("عدد ألواح السترنك زايد", 45), C("تصميم السترنك مو مناسب", 35), C("Voc ارتفع بالبرد", 20)],
    ["لا تشغل الإنفرتر قبل تصحيح التصميم.", "راجع الحد الأقصى لفولت دخل PV."],
    "حرجة",
    "PV string overvoltage",
  ],
  [
    "solar-025",
    "سترنك الألواح",
    "قطبية السترنك معكوسة",
    "هذا العطل يتعلق بـ سترنك الألواح: قطبية السترنك معكوسة. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "قطبية السترنك معكوسة",
    [C("الموجب والسالب متبدلين", 45), C("تركيب أو صيانة غلط", 35), C("تمييز الكيبلات مو واضح", 20)],
    ["لا تصحح القطبية والسترنك حي.", "خلي فني يتأكد من القطبية بعد العزل."],
    "حرجة",
    "PV reverse polarity",
  ],
  [
    "solar-026",
    "سترنكات الألواح",
    "السترنكات مو متطابقة",
    "هذا العطل يتعلق بـ سترنكات الألواح: السترنكات مو متطابقة. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "السترنكات مو متطابقة",
    [C("عدد ألواح مختلف", 45), C("أنواع ألواح مختلفة", 35), C("ظل أو زاوية مختلفة", 20)],
    ["رتب السترنكات حسب تصميم الـ MPPT.", "لا تجمع سترنكات غير متوافقة."],
    "متوسطة",
    "PV string mismatch",
  ],
  [
    "solar-027",
    "كيبلات DC",
    "أكو قطع بكيبل DC",
    "هذا العطل يتعلق بـ كيبلات DC: أكو قطع بكيبل DC. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "أكو قطع بكيبل DC",
    [C("كيبل مقطوع", 45), C("MC4 مفصول", 35), C("ترمنل مفصول", 20)],
    ["خلي السترنك معزول.", "افحص الاستمرارية بعد العزل فقط."],
    "حرجة",
    "PV DC cable open circuit",
  ],
  [
    "solar-028",
    "كيبلات DC",
    "عازل كيبل DC متضرر",
    "هذا العطل يتعلق بـ كيبلات DC: عازل كيبل DC متضرر. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "عازل كيبل DC متضرر",
    [C("الكيبل محصور تحت اللوح", 45), C("احتكاك بحافة", 35), C("قوارض أو تقادم", 20)],
    ["بدل الجزء المتضرر.", "ثبت الكيبلات بعيد عن الحواف."],
    "حرجة",
    "PV DC insulation damaged",
  ],
  [
    "solar-029",
    "كيبلات DC",
    "حرارة كيبل DC ترتفع",
    "هذا العطل يتعلق بـ كيبلات DC: حرارة كيبل DC ترتفع. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "حرارة كيبل DC ترتفع",
    [C("مقطع الكيبل صغير", 45), C("تيار أعلى من التصميم", 35), C("ربط مقاومته عالية", 20)],
    ["افحص مقطع الكيبل والتيار.", "افحص نقاط الربط إذا الحرارة محلية."],
    "حرجة",
    "PV DC cable overheating",
  ],
  [
    "solar-030",
    "كونكتر MC4",
    "MC4 مرخي",
    "هذا العطل يتعلق بـ كونكتر MC4: MC4 مرخي. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "MC4 مرخي",
    [C("كبس الطرف مو صحيح", 45), C("الكونكتر مو داخل للنهاية", 35), C("شد على الكيبل", 20)],
    ["لا تفصله تحت حمل.", "بدل أو أعد تركيب الكونكتر بأداة مناسبة."],
    "حرجة",
    "MC4 loose connector",
  ],
  [
    "solar-031",
    "كونكتر MC4",
    "أكو حرك أو ذوبان بـ MC4",
    "هذا العطل يتعلق بـ كونكتر MC4: أكو حرك أو ذوبان بـ MC4. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "أكو حرك أو ذوبان بـ MC4",
    [C("تلامس ضعيف", 45), C("كبس غلط", 35), C("كونكترات غير متوافقة", 20)],
    ["اعزل السترنك.", "بدل الكونكترات المتضررة وافحص الباقي."],
    "حرجة",
    "MC4 burnt arcing",
  ],
  [
    "solar-032",
    "كونكتر MC4",
    "MC4 من نوعين مختلفين مربوطات سوه",
    "هذا العطل يتعلق بـ كونكتر MC4: MC4 من نوعين مختلفين مربوطات سوه. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "MC4 من نوعين مختلفين مربوطات سوه",
    [C("قطع من مصنعين مختلفين", 45), C("استبدال غير مطابق", 35), C("عدم توافق الكونكترات", 20)],
    ["استخدم زوج متوافق من نفس النظام.", "راجع باقي النقاط المشابهة."],
    "حرجة",
    "MC4 incompatible connectors",
  ],
  [
    "solar-033",
    "فيوزات DC",
    "فيوز سترنك محترق",
    "هذا العطل يتعلق بـ فيوزات DC: فيوز سترنك محترق. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "فيوز سترنك محترق",
    [C("تيار عكسي أو عطل بالسترنك", 45), C("فيوز غير مناسب", 35), C("حامل الفيوز بيه مشكلة", 20)],
    ["لا تبدل الفيوز قبل معرفة السبب.", "استخدم نفس التصنيف الصحيح."],
    "عالية",
    "PV string fuse blown",
  ],
  [
    "solar-034",
    "حامل فيوز DC",
    "حرارة حامل الفيوز ترتفع",
    "هذا العطل يتعلق بـ حامل فيوز DC: حرارة حامل الفيوز ترتفع. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "حرارة حامل الفيوز ترتفع",
    [C("تلامس ضعيف", 45), C("ربط مرخي", 35), C("الحامل متضرر", 20)],
    ["لا تفتح جهاز مو مخصص للفصل تحت حمل.", "بدل الحامل إذا بيه تغير لون أو ذوبان."],
    "حرجة",
    "PV fuse holder overheating",
  ],
  [
    "solar-035",
    "Combiner Box",
    "ماي أو رطوبة داخل Combiner Box",
    "هذا العطل يتعلق بـ Combiner Box: ماي أو رطوبة داخل Combiner Box. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "ماي أو رطوبة داخل Combiner Box",
    [C("جلاندات مو محكمة", 45), C("ربل الباب متضرر", 35), C("تكاثف داخلي", 20)],
    ["اعزل المدخلات بطريقة صحيحة.", "جفف الصندوق وعالج مصدر الماء."],
    "حرجة",
    "PV combiner moisture",
  ],
  [
    "solar-036",
    "Combiner Box",
    "حرارة البار داخل Combiner ترتفع",
    "هذا العطل يتعلق بـ Combiner Box: حرارة البار داخل Combiner ترتفع. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "حرارة البار داخل Combiner ترتفع",
    [C("ربط مرخي", 45), C("تيار عالي", 35), C("أكسدة أو تآكل", 20)],
    ["اعزل الصندوق قبل الربط.", "بدل الجزء المتضرر."],
    "حرجة",
    "PV combiner busbar overheating",
  ],
  [
    "solar-037",
    "حماية DC SPD",
    "SPD مال DC متضرر",
    "هذا العطل يتعلق بـ حماية DC SPD: SPD مال DC متضرر. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "SPD مال DC متضرر",
    [C("نبضة Surge أو صاعقة", 45), C("عمر القطعة انتهى", 35), C("تأريض مو سليم", 20)],
    ["خلي فني يفحص المؤشر والتأريض.", "بدل SPD حسب مواصفات المنظومة."],
    "عالية",
    "PV DC SPD failed",
  ],
  [
    "solar-038",
    "عازل DC",
    "عازل DC ما يقبل يغلق",
    "هذا العطل يتعلق بـ عازل DC: عازل DC ما يقبل يغلق. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "عازل DC ما يقبل يغلق",
    [C("آلية العازل متضررة", 45), C("قوس داخلي", 35), C("القطعة متأثرة بالحرارة", 20)],
    ["لا تجبر العازل على الإغلاق.", "خليه لفني لأن دخل PV ممكن يبقى حي."],
    "حرجة",
    "PV DC isolator fault",
  ],
  [
    "solar-039",
    "عازل DC",
    "حرارة عازل DC ترتفع",
    "هذا العطل يتعلق بـ عازل DC: حرارة عازل DC ترتفع. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "حرارة عازل DC ترتفع",
    [C("ربط مرخي", 45), C("تلامسات متآكلة", 35), C("العازل أصغر من المطلوب", 20)],
    ["اعزل المنظومة.", "بدل العازل إذا بيه حرك أو تغير لون."],
    "حرجة",
    "PV DC isolator overheating",
  ],
  [
    "solar-040",
    "دائرة DC",
    "أكو Ground Fault بالـ PV",
    "هذا العطل يتعلق بـ دائرة DC: أكو Ground Fault بالـ PV. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "أكو Ground Fault بالـ PV",
    [C("عازل كيبل متضرر", 45), C("سلك محصور بين اللوح والهيكل", 35), C("رطوبة بكونكتر أو صندوق", 20)],
    ["لا تعيد التشغيل بدون تحديد السبب.", "خلي فني يفحص العزل والأرضي."],
    "حرجة",
    "PV ground fault earth fault",
  ],
  [
    "solar-041",
    "دائرة DC",
    "مقاومة العزل واطية",
    "هذا العطل يتعلق بـ دائرة DC: مقاومة العزل واطية. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "مقاومة العزل واطية",
    [C("رطوبة", 45), C("كيبل متضرر", 35), C("MC4 أو Junction Box بيه تسريب", 20)],
    ["لا تستمر بإعادة Reset فقط.", "خلي فني يحدد السترنك المسبب."],
    "حرجة",
    "PV insulation resistance low",
  ],
  [
    "solar-042",
    "دائرة DC",
    "أكو Arc Fault بالـ DC",
    "هذا العطل يتعلق بـ دائرة DC: أكو Arc Fault بالـ DC. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "أكو Arc Fault بالـ DC",
    [C("MC4 مرخي", 45), C("كيبل مجروح", 35), C("ترمنل ضعيف", 20)],
    ["خلي المنظومة مفصولة حسب تعليمات الجهاز.", "الفحص والإصلاح لفني مؤهل."],
    "حرجة",
    "PV DC arc fault AFCI",
  ],
  [
    "solar-043",
    "التأريض",
    "سلك أرضي المنظومة مقطوع",
    "هذا العطل يتعلق بـ التأريض: سلك أرضي المنظومة مقطوع. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "سلك أرضي المنظومة مقطوع",
    [C("سلك الأرضي مفصول", 45), C("كلامب مرخي", 35), C("تآكل بنقطة الربط", 20)],
    ["لا تشغل المنظومة بدون التأريض المطلوب.", "افحص استمرارية الأرضي."],
    "حرجة",
    "PV grounding conductor open",
  ],
  [
    "solar-044",
    "الإنفرتر الشمسي",
    "الإنفرتر ما يشتغل نهائياً",
    "هذا العطل يتعلق بـ الإنفرتر الشمسي: الإنفرتر ما يشتغل نهائياً. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "الإنفرتر ما يشتغل نهائياً",
    [C("ماكو DC من الألواح", 45), C("قاطع DC أو AC مفصول", 35), C("عطل داخلي بالإنفرتر", 20)],
    ["تأكد من القواطع والمؤشرات.", "خلي فني يفحص دخل DC وخرج AC."],
    "عالية",
    "solar inverter dead",
  ],
  [
    "solar-045",
    "الإنفرتر الشمسي",
    "الإنفرتر يتأخر الصبح حتى يشتغل",
    "هذا العطل يتعلق بـ الإنفرتر الشمسي: الإنفرتر يتأخر الصبح حتى يشتغل. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "الإنفرتر يتأخر الصبح حتى يشتغل",
    [C("فولت السترنك قريب من حد البدء", 45), C("ظل أو غبار", 35), C("سترنك ضعيف", 20)],
    ["قارن فولت PV الصباحي بمواصفات الإنفرتر.", "افحص السترنكات إذا التأخير جديد."],
    "متوسطة",
    "inverter delayed start",
  ],
  [
    "solar-046",
    "الإنفرتر الشمسي",
    "الإنفرتر يعيد تشغيل نفسه كل شوي",
    "هذا العطل يتعلق بـ الإنفرتر الشمسي: الإنفرتر يعيد تشغيل نفسه كل شوي. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "الإنفرتر يعيد تشغيل نفسه كل شوي",
    [C("فولت الشبكة خارج الحدود", 45), C("DC مو مستقر", 35), C("حرارة الإنفرتر عالية", 20)],
    ["راجع سجل الأعطال.", "افحص AC وDC والحرارة."],
    "عالية",
    "inverter repeated restart",
  ],
  [
    "solar-047",
    "الإنفرتر الشمسي",
    "الإنفرتر يعطي DC Overvoltage",
    "هذا العطل يتعلق بـ الإنفرتر الشمسي: الإنفرتر يعطي DC Overvoltage. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "الإنفرتر يعطي DC Overvoltage",
    [C("عدد ألواح السترنك زايد", 45), C("تصميم السترنك غلط", 35), C("Voc ارتفع بالبرد", 20)],
    ["لا تعيد تشغيله قبل مراجعة فولت السترنك.", "صحح التصميم."],
    "حرجة",
    "inverter DC overvoltage",
  ],
  [
    "solar-048",
    "الإنفرتر الشمسي",
    "الإنفرتر يعطي DC Undervoltage",
    "هذا العطل يتعلق بـ الإنفرتر الشمسي: الإنفرتر يعطي DC Undervoltage. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "الإنفرتر يعطي DC Undervoltage",
    [C("سترنك قصير أو لوح مفقود", 45), C("ظل شديد", 35), C("MC4 أو لوح ضعيف", 20)],
    ["قارن فولت السترنك بالمطلوب.", "افحص التوصيلات والألواح."],
    "عالية",
    "inverter DC undervoltage",
  ],
  [
    "solar-049",
    "MPPT",
    "الـ MPPT ما يطلع القدرة المطلوبة",
    "هذا العطل يتعلق بـ MPPT: الـ MPPT ما يطلع القدرة المطلوبة. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "الـ MPPT ما يطلع القدرة المطلوبة",
    [C("سترنكات غير متطابقة", 45), C("ظل جزئي", 35), C("فولت السترنك خارج مجال MPPT", 20)],
    ["راجع توزيع السترنكات على الـ MPPT.", "قارن فولت وتيار كل مدخل."],
    "عالية",
    "solar MPPT fault",
  ],
  [
    "solar-050",
    "الإنفرتر الشمسي",
    "حرارة الإنفرتر ترتفع",
    "هذا العطل يتعلق بـ الإنفرتر الشمسي: حرارة الإنفرتر ترتفع. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "حرارة الإنفرتر ترتفع",
    [C("تهوية ضعيفة", 45), C("مروحة متوقفة", 35), C("فتحات مسدودة", 20)],
    ["نظف فتحات التهوية بعد العزل.", "افحص المروحة ومكان التركيب."],
    "عالية",
    "inverter overtemperature",
  ],
  [
    "solar-051",
    "مروحة الإنفرتر",
    "مروحة الإنفرتر ما تشتغل",
    "هذا العطل يتعلق بـ مروحة الإنفرتر: مروحة الإنفرتر ما تشتغل. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "مروحة الإنفرتر ما تشتغل",
    [C("المروحة خربانة", 45), C("غبار", 35), C("تغذية المروحة بيها مشكلة", 20)],
    ["لا تستمر بحمل عالي إذا الحرارة ترتفع.", "بدل أو صلح المروحة حسب المصنع."],
    "عالية",
    "inverter fan failure",
  ],
  [
    "solar-052",
    "الشبكة AC",
    "فولت الشبكة عالي والإنفرتر يفصل",
    "هذا العطل يتعلق بـ الشبكة AC: فولت الشبكة عالي والإنفرتر يفصل. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "فولت الشبكة عالي والإنفرتر يفصل",
    [C("فولت الشبكة عالي", 45), C("Voltage rise بكيبل AC", 35), C("مقطع الكيبل مو مناسب", 20)],
    ["لا تغير حدود الحماية عشوائياً.", "افحص فولت الشبكة والكيبل."],
    "عالية",
    "grid overvoltage solar",
  ],
  [
    "solar-053",
    "الشبكة AC",
    "فولت الشبكة واطي والإنفرتر يفصل",
    "هذا العطل يتعلق بـ الشبكة AC: فولت الشبكة واطي والإنفرتر يفصل. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "فولت الشبكة واطي والإنفرتر يفصل",
    [C("الشبكة واطية", 45), C("ربط AC ضعيف", 35), C("كيبل AC بيه هبوط", 20)],
    ["افحص الشبكة والربطات.", "لا تتجاوز حماية الإنفرتر."],
    "عالية",
    "grid undervoltage solar",
  ],
  [
    "solar-054",
    "الشبكة AC",
    "تردد الشبكة عالي",
    "هذا العطل يتعلق بـ الشبكة AC: تردد الشبكة عالي. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "تردد الشبكة عالي",
    [C("مشكلة بالشبكة أو المولد", 65), C("Grid Code مو صحيح", 35)],
    ["راجع مصدر الشبكة أو المولد.", "تأكد من إعدادات Grid Code."],
    "عالية",
    "grid overfrequency solar",
  ],
  [
    "solar-055",
    "الشبكة AC",
    "تردد الشبكة واطي",
    "هذا العطل يتعلق بـ الشبكة AC: تردد الشبكة واطي. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "تردد الشبكة واطي",
    [C("مشكلة بالشبكة أو المولد", 45), C("تحميل عالي على مولد", 35), C("Grid Code مو صحيح", 20)],
    ["راجع المصدر.", "لا تغير حماية التردد عشوائياً."],
    "عالية",
    "grid underfrequency solar",
  ],
  [
    "solar-056",
    "الشبكة AC",
    "الإنفرتر يكول ماكو Grid",
    "هذا العطل يتعلق بـ الشبكة AC: الإنفرتر يكول ماكو Grid. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "الإنفرتر يكول ماكو Grid",
    [C("قاطع AC مفصول", 45), C("كيبل AC مقطوع", 35), C("الشبكة مقطوعة", 20)],
    ["تأكد من وجود الشبكة.", "خلي فني يفحص القاطع والكيبل."],
    "عالية",
    "solar inverter no grid",
  ],
  [
    "solar-057",
    "الشبكة AC",
    "الإنفرتر ما يتزامن ويا الشبكة",
    "هذا العطل يتعلق بـ الشبكة AC: الإنفرتر ما يتزامن ويا الشبكة. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "الإنفرتر ما يتزامن ويا الشبكة",
    [C("فولت أو تردد خارج الحدود", 45), C("ترتيب الفازات غلط", 35), C("Grid Code مو صحيح", 20)],
    ["راجع سجل الإنفرتر.", "افحص الفولت والتردد وتسلسل الفازات."],
    "عالية",
    "grid synchronization fault",
  ],
  [
    "solar-058",
    "جانب AC",
    "قاطع AC يفصل من يشتغل الإنفرتر",
    "هذا العطل يتعلق بـ جانب AC: قاطع AC يفصل من يشتغل الإنفرتر. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "قاطع AC يفصل من يشتغل الإنفرتر",
    [C("تيار أعلى من تحمل القاطع", 45), C("عطل بخرج الإنفرتر", 35), C("قاطع أو كيبل مو مناسب", 20)],
    ["لا تبدل القاطع بأمبير أعلى عشوائياً.", "افحص تيار الخرج والكيبل والقاطع."],
    "حرجة",
    "solar AC breaker trip",
  ],
  [
    "solar-059",
    "جانب AC",
    "النيوترال (البارد) مقطوع بخرج الإنفرتر",
    "هذا العطل يتعلق بـ جانب AC: النيوترال (البارد) مقطوع بخرج الإنفرتر. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "النيوترال (البارد) مقطوع بخرج الإنفرتر",
    [C("سلك النيوترال (البارد) مفصول", 45), C("بار البارد مرخي", 35), C("ترمنل محترق", 20)],
    ["خلي النظام مفصول إذا الخرج مو مستقر.", "افحص مسار النيوترال (البارد)."],
    "حرجة",
    "solar inverter open neutral",
  ],
  [
    "solar-060",
    "الإنفرتر الشمسي",
    "قاطع التسريب يفصل ويا الإنفرتر",
    "هذا العطل يتعلق بـ الإنفرتر الشمسي: قاطع التسريب يفصل ويا الإنفرتر. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "قاطع التسريب يفصل ويا الإنفرتر",
    [C("تسريب DC أو AC", 45), C("رطوبة أو عزل ضعيف", 35), C("نوع RCD مو مناسب للمنظومة", 20)],
    ["لا تلغي RCD حتى توقف الفصل.", "افحص التسريب ونوع الحماية حسب المصنع."],
    "حرجة",
    "solar RCD earth leakage",
  ],
  [
    "solar-061",
    "الإنفرتر الشمسي",
    "ريليه الشبكة الداخلي يعطي عطل",
    "هذا العطل يتعلق بـ الإنفرتر الشمسي: ريليه الشبكة الداخلي يعطي عطل. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "ريليه الشبكة الداخلي يعطي عطل",
    [C("ريليه داخلي متضرر", 45), C("فحص ذاتي فشل", 35), C("جهد الشبكة مو طبيعي", 20)],
    ["راجع كود العطل.", "خلي خدمة مختصة تفحص الجهاز."],
    "عالية",
    "inverter relay fault",
  ],
  [
    "solar-062",
    "المراقبة",
    "الإنفرتر Offline بالمراقبة",
    "هذا العطل يتعلق بـ المراقبة: الإنفرتر Offline بالمراقبة. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "الإنفرتر Offline بالمراقبة",
    [C("الإنترنت مقطوع", 45), C("Dongle متوقف", 35), C("إعداد الشبكة تغير", 20)],
    ["تأكد من الإنترنت والجهاز محلياً.", "أعد إعداد الاتصال فقط."],
    "منخفضة",
    "solar monitoring offline",
  ],
  [
    "solar-063",
    "إعدادات الإنفرتر",
    "إعدادات الإنفرتر مو مناسبة للمنظومة",
    "هذا العطل يتعلق بـ إعدادات الإنفرتر: إعدادات الإنفرتر مو مناسبة للمنظومة. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "إعدادات الإنفرتر مو مناسبة للمنظومة",
    [C("Grid Code غلط", 45), C("حدود البطارية غلط", 35), C("إعداد MPPT أو Export غلط", 20)],
    ["احتفظ بنسخة من الإعدادات.", "طابق الإعدادات ويا تصميم المنظومة."],
    "عالية",
    "solar inverter incorrect settings",
  ],
  [
    "solar-064",
    "Microinverter",
    "Microinverter واحد Offline",
    "هذا العطل يتعلق بـ Microinverter: Microinverter واحد Offline. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "Microinverter واحد Offline",
    [C("الوحدة متضررة", 45), C("كونكتر AC أو DC مفصول", 35), C("مشكلة تواصل", 20)],
    ["حدد الوحدة من الخريطة.", "خلي فني يفحص التوصيلات والوحدة."],
    "عالية",
    "microinverter offline",
  ],
  [
    "solar-065",
    "Optimizer",
    "Optimizer واحد ما يطلع إنتاج",
    "هذا العطل يتعلق بـ Optimizer: Optimizer واحد ما يطلع إنتاج. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "Optimizer واحد ما يطلع إنتاج",
    [C("Optimizer متضرر", 45), C("MC4 مفصول", 35), C("اللوح نفسه ضعيف", 20)],
    ["قارن بيانات الوحدة بجاراتها.", "افحص اللوح والـ Optimizer."],
    "عالية",
    "solar optimizer no output",
  ],
  [
    "solar-066",
    "البطارية",
    "البطارية ما تشحن",
    "هذا العطل يتعلق بـ البطارية: البطارية ما تشحن. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "البطارية ما تشحن",
    [C("BMS مانع الشحن", 45), C("فيوز أو قاطع البطارية مفصول", 35), C("إعدادات الشحن غلط", 20)],
    ["لا تتجاوز BMS.", "افحص القواطع والاتصال وإعدادات الشحن."],
    "عالية",
    "solar battery not charging",
  ],
  [
    "solar-067",
    "البطارية",
    "تيار شحن البطارية واطي",
    "هذا العطل يتعلق بـ البطارية: تيار شحن البطارية واطي. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "تيار شحن البطارية واطي",
    [C("الإنتاج الشمسي واطي", 45), C("حد الشحن مضبوط قليل", 35), C("BMS محدد التيار", 20)],
    ["راجع حدود الشحن وإنتاج PV.", "افحص الكيبلات والـ BMS."],
    "متوسطة",
    "solar battery low charge current",
  ],
  [
    "solar-068",
    "البطارية",
    "فولت البطارية عالي",
    "هذا العطل يتعلق بـ البطارية: فولت البطارية عالي. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "فولت البطارية عالي",
    [C("إعداد فولت الشحن عالي", 45), C("شاحن أو منظم متضرر", 35), C("BMS أو قراءة الفولت بيها مشكلة", 20)],
    ["وقف الشحن إذا الفولت خارج حدود المصنع.", "راجع منظومة الشحن."],
    "حرجة",
    "solar battery overvoltage",
  ],
  [
    "solar-069",
    "البطارية",
    "فولت البطارية واطي كلش",
    "هذا العطل يتعلق بـ البطارية: فولت البطارية واطي كلش. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "فولت البطارية واطي كلش",
    [C("تفريغ عميق", 45), C("ماكو شحن كافي", 35), C("خلية ضعيفة", 20)],
    ["قلل الأحمال.", "اشحن حسب تعليمات البطارية."],
    "عالية",
    "solar battery undervoltage",
  ],
  [
    "solar-070",
    "البطارية",
    "البطارية تفرغ بسرعة",
    "هذا العطل يتعلق بـ البطارية: البطارية تفرغ بسرعة. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "البطارية تفرغ بسرعة",
    [C("سعة البطارية تدهورت", 45), C("حمل أعلى من المتوقع", 35), C("SOC مو معاير", 20)],
    ["قارن الطاقة المسحوبة بسعة البنك.", "افحص صحة البطارية وSOC."],
    "عالية",
    "solar battery fast discharge",
  ],
  [
    "solar-071",
    "البطارية",
    "حرارة البطارية ترتفع",
    "هذا العطل يتعلق بـ البطارية: حرارة البطارية ترتفع. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "حرارة البطارية ترتفع",
    [C("تيار شحن أو تفريغ عالي", 45), C("تهوية ضعيفة", 35), C("خلية متضررة", 20)],
    ["وقف الشحن أو الحمل حسب تعليمات المصنع.", "لا تفتح بطارية ليثيوم."],
    "حرجة",
    "solar battery overtemperature",
  ],
  [
    "solar-072",
    "بطارية ليثيوم",
    "الخلايا مو متوازنة",
    "هذا العطل يتعلق بـ بطارية ليثيوم: الخلايا مو متوازنة. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "الخلايا مو متوازنة",
    [C("خلية ضعيفة", 45), C("Balancing ما يكفي", 35), C("شحن غير مناسب", 20)],
    ["لا تتجاوز BMS.", "خلي خدمة البطارية تقرأ فرق الخلايا."],
    "عالية",
    "lithium cell imbalance solar",
  ],
  [
    "solar-073",
    "BMS",
    "الـ BMS فصل البطارية",
    "هذا العطل يتعلق بـ BMS: الـ BMS فصل البطارية. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "الـ BMS فصل البطارية",
    [C("فولت خلية عالي أو واطي", 45), C("حرارة غير طبيعية", 35), C("تيار زائد", 20)],
    ["اقرأ سبب الفصل من BMS.", "عالج السبب قبل Reset."],
    "عالية",
    "solar battery BMS disconnect",
  ],
  [
    "solar-074",
    "اتصال البطارية",
    "الإنفرتر ما يتواصل ويا البطارية",
    "هذا العطل يتعلق بـ اتصال البطارية: الإنفرتر ما يتواصل ويا البطارية. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "الإنفرتر ما يتواصل ويا البطارية",
    [C("كيبل CAN أو RS485 مفصول", 45), C("Protocol غلط", 35), C("BMS أو الإنفرتر مو متوافق", 20)],
    ["راجع كيبل الاتصال والترتيب.", "طابق الـ Protocol حسب المصنع."],
    "عالية",
    "solar battery communication fault",
  ],
  [
    "solar-075",
    "منظم الشحن",
    "حرارة منظم الشحن ترتفع",
    "هذا العطل يتعلق بـ منظم الشحن: حرارة منظم الشحن ترتفع. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "حرارة منظم الشحن ترتفع",
    [C("تهوية ضعيفة", 45), C("تيار قريب من الحد", 35), C("ربط حرارته عالية", 20)],
    ["خفف الحمل إذا لازم.", "حسن التهوية وافحص الربطات."],
    "عالية",
    "charge controller overheating",
  ],
  [
    "solar-076",
    "منظم الشحن",
    "فولت PV أعلى من تحمل منظم الشحن",
    "هذا العطل يتعلق بـ منظم الشحن: فولت PV أعلى من تحمل منظم الشحن. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "فولت PV أعلى من تحمل منظم الشحن",
    [C("عدد ألواح السترنك زايد", 45), C("تصميم السترنك غلط", 35), C("Voc ارتفع بالبرد", 20)],
    ["لا تشغل المنظم قبل تصحيح السترنك.", "راجع الحد الأقصى لدخل PV."],
    "حرجة",
    "charge controller PV overvoltage",
  ],
  [
    "solar-077",
    "خرج Backup / EPS",
    "ماكو كهرباء من خرج الـ Backup وقت الانقطاع",
    "هذا العطل يتعلق بـ خرج Backup / EPS: ماكو كهرباء من خرج الـ Backup وقت الانقطاع. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "ماكو كهرباء من خرج الـ Backup وقت الانقطاع",
    [C("البطارية فارغة أو مفصولة", 45), C("إعداد Backup مو مفعل", 35), C("قاطع EPS مفصول", 20)],
    ["تأكد من حالة البطارية والقاطع.", "راجع إعدادات الـ Backup."],
    "عالية",
    "solar EPS backup no output",
  ],
  [
    "solar-078",
    "ATS / Changeover",
    "التحويل ما ينتقل للطاقة الاحتياطية",
    "هذا العطل يتعلق بـ ATS / Changeover: التحويل ما ينتقل للطاقة الاحتياطية. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "التحويل ما ينتقل للطاقة الاحتياطية",
    [C("ATS أو الكونتاكتور متضرر", 45), C("ماكو إشارة مصدر احتياطي", 35), C("دائرة السيطرة بيها عطل", 20)],
    ["تأكد من توفر المصدر الاحتياطي.", "خلي فني يفحص ATS والـ Interlock."],
    "حرجة",
    "solar ATS no transfer",
  ],
  [
    "solar-079",
    "نظام المراقبة",
    "بيانات الإنتاج بيها فراغات",
    "هذا العطل يتعلق بـ نظام المراقبة: بيانات الإنتاج بيها فراغات. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "بيانات الإنتاج بيها فراغات",
    [C("الإنترنت انقطع", 45), C("Data Logger توقف", 35), C("مشكلة وقت أو سيرفر", 20)],
    ["تأكد من الاتصال والذاكرة.", "قارن العداد المحلي قبل اعتبارها خسارة إنتاج."],
    "منخفضة",
    "solar monitoring data gaps",
  ],
  [
    "solar-080",
    "أداء المنظومة",
    "الإنتاج السنوي أقل من المتوقع بدون إنذار واضح",
    "هذا العطل يتعلق بـ أداء المنظومة: الإنتاج السنوي أقل من المتوقع بدون إنذار واضح. راقب القراءات وسجل الأعطال وقارن الأداء ويا الوضع الطبيعي للمنظومة.",
    "الإنتاج السنوي أقل من المتوقع بدون إنذار واضح",
    [C("غبار أو ظل", 45), C("تدهور ألواح", 35), C("سترنكات ضعيفة", 20)],
    ["قارن الإنتاج ببيانات الطقس والفترات السابقة.", "اعمل فحص O&M للسترنكات والألواح والإنفرتر."],
    "متوسطة",
    "solar system underperformance",
  ],
];

export const solarFaults: SolarFaultLocal[] = rows.map(([id,device_ar,name_ar,description_ar,symptom,causes,solutions,severity,keywords]) => {
  const critical = severity === "حرجة";
  return {
    id, category_id: null, device_ar, name_ar, description_ar, symptoms:[symptom], causes,
    tests:[{
      test:"الفحص الأولي", location:device_ar,
      method:"تنبيه مهم: دوائر PV ممكن تبقى تحت فولت بمجرد وجود ضوء على الألواح، حتى لو AC مطفي. لا تفصل MC4 أو فيوز أو قطعة مو مخصصة للفصل تحت حمل. العزل والقياسات على DC أو AC الحيّة خليها لفني طاقة شمسية مؤهل وبمعدات مناسبة.",
      normal:"ماكو آثار حرك أو حرارة عالية أو رطوبة أو عزل متضرر، والقراءات والإنتاج ضمن الطبيعي مقارنة بتصميم المنظومة والجو.",
      faulty:"وجود قوس أو تغير لون أو حرارة عالية أو Ground Fault أو عزل ضعيف أو فرق كبير بين سترنكات متشابهة يدل على عطل يحتاج إصلاح."
    }],
    solutions, severity,
    safety_warnings: critical ? [
      "خطر صعقة وقوس DC: الألواح تنتج كهرباء من تتعرض للضوء، لذلك لا تعتبر كيبلات PV ميتة لمجرد إطفاء الإنفرتر أو قاطع AC.",
      "عزل السترنكات وفتح صناديق DC والبطاريات والإنفرتر والقياس على أجزاء حيّة خليه لفني مؤهل ويطبق Lockout/Tagout وتعليمات المصنع."
    ] : [
      "افصل وعزل مصادر الطاقة قبل الفك أو إعادة الربط، وانتبه أن جهة PV ممكن تبقى مولدة تحت الضوء.",
      "إذا شفت شرر أو ذوبان أو تغير لون أو شميت ريحة حرك، وقف المنظومة وخلي فني يفحصها."
    ],
    error_code:null, keywords, next_question_key:null
  };
});

export const solarIssues: SolarIssueLocal[] = solarFaults.map((fault,index)=>({
  id:fault.id, title_ar:fault.name_ar, description_ar:fault.description_ar, causes:fault.causes,
  steps:[...fault.solutions],
  measurements:[
    "قارن القراءات ويا مواصفات الجهاز أو التصميم والقراءات التاريخية لنفس الظروف.",
    "أي قياس DC/AC حي أو فحص عزل يحتاج فني مؤهل وأداة مصنفة للفولت والطاقة المتوقعة."
  ],
  solution_ar:fault.solutions.join(" "), severity:fault.severity,
  safety_ar:fault.safety_warnings.join(" "), keywords:fault.keywords, sort_order:index+1
}));
